/**
 * Path helpers, shared identifiers, and the shard-count constant
 * for the v2 section signup data model.
 *
 * The v2 model lives under `course/{courseId}/sectionSignupsV2/...`
 * and comprises three kinds of documents:
 *
 *  - **`slCache`** — a single doc mapping each active section
 *    leader's userId to their current slot bucket, maintained
 *    reactively by the sections onWrite trigger.
 *
 *  - **`studentShard-{0..N-1}`** — `N` shards holding per-slot
 *    student count aggregates (not per-user entries, for privacy
 *    and cheap listener payloads). The shard a student lands in
 *    is `getSectionSignupV2StudentShardIndex`.
 *
 *  - **`meta`** — a single doc holding the dynamic per-pool
 *    student targets, bump configuration, and bump event log.
 *
 * No Firestore dependency; safe from client, admin, or pure code.
 */
import {hashStringTo32BitSha256} from '../../hash';
import type {CourseId, UserId} from '../../types/branded';

/**
 * The section-signup pool a slot belongs to. Adults (`main`) and
 * minors (`minor`) are completely separate pools and never share
 * section leaders, students, targets, or counters.
 */
export type SectionSignupV2Type = 'main' | 'minor';

/** All valid {@link SectionSignupV2Type} values, useful for iteration. */
export const SECTION_SIGNUP_V2_TYPES: readonly SectionSignupV2Type[] = [
  'main',
  'minor',
] as const;

/**
 * How many shards back the student count cache for each course.
 *
 * Imported everywhere the shard count matters — the onboarding
 * trigger, the shared listener hook, the seed callable, the pure
 * aggregation helper, and tests — so this number is changed in
 * exactly one place.
 *
 * v2 starts at 10 to keep the per-write trigger read cost low
 * (`slCache` + N shards + `meta` = 13 reads per fire at 10
 * shards). If real enrollment storms push past ~10 sustained
 * student writes per second per course, raise this constant and
 * re-run the seed callable — no other code has to change.
 */
export const SECTION_SIGNUP_V2_STUDENT_SHARD_COUNT = 10;

/** The subcollection under each course that holds all v2 docs. */
export const SECTION_SIGNUPS_V2_SUBCOLLECTION = 'sectionSignupsV2' as const;

/** The fixed document id of the per-course SL cache doc. */
export const SECTION_SIGNUP_V2_SL_CACHE_DOC_ID = 'slCache' as const;

/** The fixed document id of the per-course meta doc. */
export const SECTION_SIGNUP_V2_META_DOC_ID = 'meta' as const;

/** Prefix for student shard doc ids. */
export const SECTION_SIGNUP_V2_STUDENT_SHARD_PREFIX = 'studentShard-' as const;

/**
 * Produces the full Firestore path to the subcollection containing
 * all v2 docs (slCache + shards + meta) for a single course.
 */
export function getSectionSignupV2SubcollectionPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `course/${courseId}/${SECTION_SIGNUPS_V2_SUBCOLLECTION}`;
}

/** Full Firestore path to the per-course `slCache` doc. */
export function getSectionSignupV2SlCachePath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${getSectionSignupV2SubcollectionPath({courseId})}/${SECTION_SIGNUP_V2_SL_CACHE_DOC_ID}`;
}

/** Full Firestore path to the per-course `meta` doc. */
export function getSectionSignupV2MetaPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${getSectionSignupV2SubcollectionPath({courseId})}/${SECTION_SIGNUP_V2_META_DOC_ID}`;
}

/**
 * Produces the document id for a student shard doc given its shard
 * index. The id format is stable and human-readable so admins can
 * find a specific shard in the Firestore console.
 *
 * Example: `getSectionSignupV2StudentShardId({shardIndex: 3})` →
 * `'studentShard-3'`.
 */
export function getSectionSignupV2StudentShardId({
  shardIndex,
}: {
  shardIndex: number;
}): string {
  return `${SECTION_SIGNUP_V2_STUDENT_SHARD_PREFIX}${String(shardIndex)}`;
}

/**
 * The shard a student's pick is counted in. Every writer of a student's
 * counts (the `setSectionTime` callable and the `initSectionSignupV2` reseed)
 * must bucket through this so a later increment lands where the seed put the
 * student.
 *
 * @param params.userId - The student's user id.
 * @return An index in `[0, SECTION_SIGNUP_V2_STUDENT_SHARD_COUNT)`.
 * @throws Never.
 */
export function getSectionSignupV2StudentShardIndex({
  userId,
}: {
  userId: UserId;
}): number {
  return (
    Math.abs(hashStringTo32BitSha256(userId)) %
    SECTION_SIGNUP_V2_STUDENT_SHARD_COUNT
  );
}

/** Full Firestore path to a specific student shard doc. */
export function getSectionSignupV2StudentShardPath({
  courseId,
  shardIndex,
}: {
  courseId: CourseId;
  shardIndex: number;
}): string {
  return `${getSectionSignupV2SubcollectionPath({courseId})}/${getSectionSignupV2StudentShardId({shardIndex})}`;
}

/**
 * Builds the `${type}_${timeIndex}` key used throughout the v2
 * data model to address a specific slot bucket. Exported so the
 * trigger, the pure aggregation helper, and tests all agree on
 * the exact format.
 */
export function buildSectionSignupV2SlotKey({
  type,
  timeIndex,
}: {
  type: SectionSignupV2Type;
  timeIndex: number;
}): string {
  return `${type}_${String(timeIndex)}`;
}
